import { useState } from "react";
import Image from "next/image";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import ExternalLinkIcon from "@/v2-components/core/icon/external-link-icon";
import {
  AppStoreIcon,
  GooglePlayStoreIcon,
  FileDownloadIcon,
  MetaStoreIcon,
  PicoStoreIcon,
  ViveportStoreIcon,
  WindowsStoreIcon,
} from "./sw-store-icons";
import { CompatibleVrHeadsetsSheets } from "@/v2-components/pages/games/CompatibleVRHeadsetsSheets";
import type { PlayerSection } from "./sw-config";

/** 있는 심볼만 쓴다. 없는 스토어는 첫 글자 배지로 남긴다(자산이 오면 여기만 는다). */
function StoreIcon({ name }: { name: string }) {
  // 크기는 호출처 반응형 — 모바일 32 · 데스크탑 60(시안 실측).
  const cls = "size-8 text-coolgray-100 laptop:size-[60px]";
  if (name === "Meta Store") return <MetaStoreIcon className={cls} />;
  if (name === "PICO Store") return <PicoStoreIcon className={cls} />;
  if (name === "VIVEPORT") return <ViveportStoreIcon className={cls} />;
  if (name === "App Store") return <AppStoreIcon className={cls} />;
  if (name === "Google Play") return <GooglePlayStoreIcon className={cls} />;
  if (name === "Windows") return <WindowsStoreIcon className={cls} />;
  /**
   * macOS 는 **Apple 로고**를 쓴다 — 시안이 래스터(1000×1000 PNG pattern)라 옮기는
   * 과정에서 base64 가 깨졌다(디코드 결과 `natural=0×0`, PNG 청크 파싱 실패).
   * App Store 와 같은 마크이므로 그 path 를 재사용한다 — `currentColor` 도 받는다.
   */
  if (name === "macOS") return <AppStoreIcon className={cls} />;
  return (
    <span className="flex size-full items-center justify-center rounded-md bg-coolgray-100 text-white">
      {name.charAt(0)}
    </span>
  );
}

/**
 * 플레이어 섹션 하나 — 배너 · 지원기기(또는 연결구조) · 다운로드 목록.
 * 시안 `29889:102325`(PC · 데스크탑) · `30035:23219`(PC · 모바일). VR/Mobile 도 같은 골격이다.
 *
 * ```
 *              데스크탑                        모바일
 * 컨테이너      gap-32                          gap-20 · px-16
 * 배너          h280 r16 · 글자 좌측 60 상단 88   p(20,32) r16 · gap-12
 * 배너 제목      36/44 white                     24/32 white
 * 배너 설명      16/22 white                     12/16 cg-30
 * 소제목        22/28 cg-100                    16/22 w500 cg-90
 * 소제목 설명    14/18 cg-60                     12/16 cg-50
 * 스토어 카드    w704 r16 보더 cg-20 · py-30      전체폭 r12 p16 · 간격 12
 *              아이콘 60 · 이름 24/32           아이콘 32 r6 · 이름 16/22 w500
 *              버튼 h42 r4 보더 cg-90            버튼 h36 r8 보더 cg-20
 * ```
 *
 * ⚠️ **Mobile 섹션만 이 블록이 「연결 구조」다**(`29889:102412`) — 제목·설명이 다르고
 * 「호환 헤드셋 전체 보기」 링크가 없다. 그래서 `support` 를 설정으로 받는다.
 */
export default function SwPlayerSection({
  section,
}: {
  section: PlayerSection;
}) {
  const { t } = useTrans(["software"]);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <section
      id={section.anchor}
      /* 앵커 이동 시 sticky 헤더에 가리지 않게 여유를 준다. */
      className="flex w-full scroll-mt-28 justify-center"
    >
      <div className="mx-4 flex w-full max-w-screen-max flex-col gap-5 laptop:gap-8">
        {/**
         * 히어로 — ★**폭마다 구조가 다르다**(시안 개정 2026-09-02).
         *
         * ```
         *   데스크탑 `29889:102326`  배너 1440×280 · 이미지 위에 카피 오버레이(x=60)
         *   모바일   `30175:156298`  ★이미지 h140 **아래** 밝은 카드 328×108
         * ```
         *
         * 종전 구현은 두 폭 다 「이미지 위 오버레이」였다. 개정판 모바일은 카피가
         * **이미지 밖 `#f2f4f7` 카드**로 내려왔고, 그래서 값이 전부 다르다:
         *
         * ```
         *              데스크탑            모바일(개정)
         *   위치        이미지 위 오버레이   이미지 아래 카드
         *   배경        없음                #f2f4f7 (coolgray-10)
         *   제목        36/44 흰색·검정      22/28 #121619
         *   설명        16/22 tr.5          12/16 tr.4 #21272a
         * ```
         *
         * 실측 결과 이 구조 차이가 모바일 섹션 높이 부족의 전부였다
         * (PC −104 · VR −130 · Mobile −128 → 카드 108 + 여백).
         *
         * ⚠️ **한 마크업으로 두 폭을 덮지 않는다.** 색·크기·배치가 전부 갈리므로
         * `laptop:` 조건을 겹쳐 쓰면 어느 쪽이 무엇인지 읽을 수 없다.
         */}

        {/**
         * ── 모바일 `30175:156298` — 이미지 + 밝은 카드 · 전체 r16 ──
         *
         * ## ★경계는 `laptop`(1024)이다 — 640 을 재보고 되돌렸다 (2026-09-04)
         *
         * 모바일 배너 자산은 **656×280**(시안 328×140 의 2배)이라 `328 CSS @ DPR2`
         * 에서만 정확하다. 이 블록이 1023 까지 덮으므로 폭이 커질수록 업스케일된다:
         *
         * ```
         *   뷰포트   박스(CSS)    업스케일 @2x   @3x
         *   360     328×140      1.00x         1.50x
         *   640     608×259      1.85x         2.78x
         *   1023    991×423      3.02x         4.53x
         * ```
         *
         * 그래서 「640 부터 데스크탑 배너로 넘기자」를 시도했는데 **구도가 깨져서
         * 되돌렸다.** 데스크탑 자산은 비율 5.14 라 좁은 박스에서 가로로 잘린다:
         *
         * ```
         *   뷰포트   박스비    가로 잘림
         *   640     2.17      58%   ← 모니터(주제)가 1/3만 남는다
         *   768     2.63      49%
         *   1023    3.54      31%
         *   1440    5.03       2%
         *   잘림 0 = 1472px 부터
         * ```
         *
         * 즉 **업스케일(흐림)과 크롭(구도 손실)의 교환**인데, 640~1023 구간에서는
         * 크롭 쪽 손실이 더 크다. 근본 해법은 **더 큰 모바일 export**(같은 328:140
         * 구도로 4x 이상)이고, 그게 오면 파일만 바꾸면 된다 — 코드는 그대로다.
         */}
        <div className="flex flex-col overflow-hidden rounded-2xl laptop:hidden">
          {/**
           * ★**높이가 아니라 비율로 잡는다** — `h-[140px]` 고정은 360 에서만 맞았다.
           *
           * 시안(`30175:156300`)은 360 폭에서 328×140 이고, 자산도 그 비율의 2배
           * (656×280)다. 폭은 `w-full` 로 흐르는데 높이만 고정하면 **360 을 벗어나는
           * 순간 박스 비율이 커져 `object-cover` 가 상하를 잘라낸다**:
           *
           * ```
           *   뷰포트   박스        박스비    세로 잘림
           *   360     328×140     2.343     0      ← 시안과 일치
           *   390     358×140     2.557     13px
           *   430     398×140     2.843     30px   ← 작성자 제보 화면
           *   768     736×140     5.257     174px
           * ```
           *
           * 이 배너는 사진이 아니라 **합성 그래픽**(플레이어 UI 패널)이라 잘리면
           * 구도가 깨진다 — 형제 카드들이 `h-[170px]` 고정인 것과 성격이 다르다.
           *
           * ⚠️ 이 블록은 `laptop:hidden` 이라 **0~1023 전 구간**을 덮는다. 고정 높이의
           * 오차가 폭에 비례해 커지는 구간이 그만큼 넓다.
           */}
          <div className="relative aspect-[328/140] w-full">
            <Image
              src={section.banner.mobile}
              alt=""
              fill
              sizes="100vw"
              className="object-cover"
            />
          </div>
          {/* `30175:156294` — p20 · bg coolgray-10.
              ★개정 시안은 **텍스트 묶음↔버튼 16**, 제목↔설명 8 이다
              (`30376:64122` gap8 안쪽 · `30175:156294` gap16 바깥). */}
          <div className="flex flex-col gap-4 bg-coolgray-10 p-5">
            <div className="flex flex-col gap-2">
              {/* `30175:156295` — 22/28 coolgray-100 */}
              <h2 className="text-2xl leading-[28px] text-coolgray-100">
                {t(section.title)}
              </h2>
              {/* `30175:156296` — 12/16 tr.4 coolgray-90. 두 폭이 같은 문장이다. */}
              <p className="whitespace-pre-line text-sm tracking-[0.4px] text-coolgray-90">
                {t(section.description)}
              </p>
            </div>
            {/**
             * ★**「호환 헤드셋 전체 보기」 — 파란 알약 버튼** (시안 개정 2026-09-07).
             *
             * 종전엔 배너 **아래** `support` 블록의 텍스트 링크(`›` 셰브론)였다. 개정 시안이
             * 그 블록을 통째로 숨기고(데스크탑 `29889:102331` · 모바일 `30035:23223` 둘 다
             * `hidden="true"`) 이 자리로 올렸다 — 여는 것은 같은 헤드셋 시트다.
             *
             * 시안 `#0f62fe` · h40 · px24 · r44 · 라벨 12/16 SemiBold tr.1 white.
             * 표기는 형제(`PcPlayerDownload`)와 같은 알약을 그대로 쓴다.
             *
             * ⚠️ **폭이 두 폭에서 다르다** — 모바일 `w-full`(`30376:64119`),
             * 데스크탑은 내용 폭(`30376:64116` shrink-0).
             */}
            {section.bannerHeadsetCta && (
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="flex h-10 w-full items-center justify-center rounded-full bg-blue-60 px-6 text-sm font-600 tracking-[0.1px] text-white transition-colors hover:bg-blue-70"
              >
                {t("View All Compatible Headsets")}
              </button>
            )}
          </div>
        </div>

        {/**
         * ── 데스크탑 `29889:102326` — 배너 1440×280 · 카피 오버레이 ──
         *
         * ⚠️ 이 자산은 비율 5.14 라 **1472px 미만에서는 항상 가로로 잘린다** —
         * 1024 에서 31%, 1440 에서 2%. 선행 동작이고 시안 폭(1440)에서는 거의
         * 문제가 없다. 경계를 낮추면 잘림이 급격히 커진다(위 모바일 블록 주석).
         */}
        <div className="relative hidden overflow-hidden rounded-2xl laptop:flex laptop:h-[280px] laptop:flex-col laptop:justify-center laptop:gap-4 laptop:px-[60px]">
          <Image
            src={section.banner.desktop}
            alt=""
            fill
            sizes="1440px"
            className="object-cover"
          />
          {/* ★Mobile Player 배너만 밝아서 글자가 검다(시안 `29889:102409`). */}
          <div
            className={cn(
              "relative flex flex-col gap-4 laptop:w-[440px]",
              section.bannerTextDesktop === "dark"
                ? "text-coolgray-100"
                : "text-white",
            )}
          >
            <h2 className="text-6xl">{t(section.title)}</h2>
            <p className="whitespace-pre-line text-lg tracking-[0.5px]">
              {/* 두 폭이 같은 문장이다 — 시안 개정 2026-09-03(`sw-config.ts` 의 `description`). */}
              {t(section.description)}
            </p>
            {/* 데스크탑은 **내용 폭**이다(`30376:64116` shrink-0) — 위 CTA 주석 참조. */}
            {section.bannerHeadsetCta && (
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="flex h-10 w-fit items-center justify-center rounded-full bg-blue-60 px-6 text-sm font-600 tracking-[0.1px] text-white transition-colors hover:bg-blue-70"
              >
                {t("View All Compatible Headsets")}
              </button>
            )}
          </div>
        </div>

        {/**
         * 지원 기기 / 연결 구조 — ★**개정 시안에서 두 폭의 구성이 같아졌다**(2026-09-03).
         *
         * ```
         *        데스크탑 29889:102282        모바일 30035:23192
         * PC     ★블록 전체 hidden(2026-09-07)  ★블록 전체 hidden
         * VR     제목만(아래 HMD 3종 행)       ★블록 전체 hidden
         * Mobile ★블록 전체 hidden(2026-09-07)  ★블록 전체 hidden
         * ```
         *
         * ★**남은 소비처는 VR 하나뿐이다** — PC 와 Mobile 은 개정 시안에서 이 블록이
         * 통째로 빠졌다(`29889:102331` · `29889:102412` 둘 다 `hidden="true"`).
         * PC 의 트리거는 배너 CTA 로 옮겼고, Mobile 의 「Connection Setup」은 소비처가
         * 0 이 되어 문구까지 걷었다.
         *
         * 종전 구현은 **모바일 제목을 「Supported Devices」로 하드코딩**하고 설명은
         * `supportMobileDescription` 이라는 별도 필드로 받았다. 그때 모바일 시안 셋이
         * 구조가 같았기 때문인데(`30035:23250`·`23251`·`23282`), 그 뒤 섹션마다 갈렸고
         * 지금은 **VR 만 남았다**.
         *
         * 그래서 **한 블록**으로 합치고 `section.support` 를 두 폭이 함께 읽는다.
         * `supportMobileDescription`(3건)은 소비처가 0 이 되어 걷었다.
         *
         * ## ★링크의 **위치**가 폭마다 다르다 — 타이포만 다른 게 아니었다
         *
         * ```
         *   모바일 `30035:23223` (h66 · gap6)   데스크탑 `29889:102331` (h58)
         *     제목        y=0                     dev-txt  x=0    제목 + 설명(y=40)
         *     설명        y=28                    link     x=1206 y=17 (우측·수직중앙)
         *     ★링크      y=50  x=0
         * ```
         *
         * 즉 모바일은 **세 줄 세로 스택**이고 데스크탑은 **좌(제목+설명) / 우(링크)** 다.
         * 초판은 제목과 링크를 한 flex row 에 넣었는데, 그러면 모바일에서
         * 「Supported Devices」가 **두 줄로 감긴다**(실측 360 에서 확인).
         *
         * → 제목+설명을 안쪽 열로 묶고 **바깥을 모바일 열 / 데스크탑 행**으로 돌린다.
         *   두 컨테이너가 다 `gap-1.5` 라 모바일 세 줄 간격이 시안의 6 으로 균일해진다.
         */}
        {section.support && (
          <div
            className={cn(
              "flex flex-col gap-1.5 laptop:flex-row laptop:items-center laptop:justify-between laptop:gap-4",
              // ★VR 만 모바일에서 숨는다(`30035:23250` hidden).
              section.support.mobileHidden && "hidden laptop:flex",
            )}
          >
            <div className="flex flex-col gap-1.5 laptop:gap-3">
              {/* 모바일 `30035:23225` 16/22 M tr.15 cg-90 · 데스크탑 22/28 cg-100 */}
              <h3 className="text-lg font-500 tracking-[0.15px] text-coolgray-90 laptop:text-2xl laptop:font-400 laptop:tracking-normal laptop:text-coolgray-100">
                {t(section.support.title)}
              </h3>
              {/* 모바일 `30035:23227` 12/16 tr.4 cg-50 · 데스크탑 14/18 cg-60. VR 은 없다. */}
              {section.support.description && (
                <p className="text-sm tracking-[0.4px] text-coolgray-50 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-60">
                  {t(section.support.description)}
                </p>
              )}
            </div>
          </div>
        )}

        {/**
         * ★기기 행 — VR Player 에만 있다(시안 `29889:102365`).
         * 초판에서 세 섹션을 같은 골격으로 그리며 이 블록을 통째로 빠뜨렸다.
         * 모델명은 종전 `VrpSupportedHeadsetsSection` 의 `VRP_HEADSETS` 와 같은 값이다
         * (그 섹션은 개편 뒤 렌더 경로가 없어 2026-10-01 에 지웠다 — 값은 `sw-config` 가 갖는다).
         */}
        {section.devices && (
          /**
           * ★**데스크탑 전용이다.** 모바일 시안(`30035:23246` `sw-vr-player`)의 자식은
           * `band · support · dl-head · dl-list` 넷뿐 — 이미지 행이 없다. 바로 위
           * `support` 텍스트가 같은 정보를 담는다.
           */
          <div className="hidden gap-6 laptop:flex laptop:flex-row laptop:gap-8">
            {section.devices.map((device) => (
              <div
                key={device.platform}
                className="flex flex-1 flex-col items-center gap-2 text-center"
              >
                {/* 시안 `29889:102369` — 이미지 박스 200×158. */}
                {/* 시안 `29889:102369` — 200×158. 자산은 2배(400×316) 투명 PNG. */}
                <div className="relative h-[158px] w-[200px]">
                  <Image
                    src={device.image}
                    alt=""
                    fill
                    sizes="200px"
                    className="object-contain"
                  />
                </div>
                {/* 시안 `29889:102371` — 16/22 SemiBold cg-100 · 12/16 cg-60 · 갭 8. */}
                <p className="text-lg font-600 tracking-[0.5px] text-coolgray-100">
                  {device.platform}
                </p>
                <p className="text-sm tracking-[0.4px] text-coolgray-60">
                  {device.models}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* 다운로드 */}
        <div className="flex flex-col gap-1.5 laptop:gap-3">
          <h3 className="text-lg font-500 tracking-[0.15px] text-coolgray-90 laptop:text-2xl laptop:font-400 laptop:tracking-normal laptop:text-coolgray-100">
            {t(section.download.title)}
          </h3>
          <p className="text-sm tracking-[0.4px] text-coolgray-50 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-60">
            {t(section.download.description)}
          </p>
        </div>

        <div className="flex flex-col gap-3 laptop:flex-row laptop:gap-8">
          {section.stores.map((store) => (
            <a
              key={store.name}
              href={store.href}
              target="_blank"
              rel="noreferrer"
              className="flex flex-1 items-center gap-3 rounded-xl border border-coolgray-20 p-4 laptop:gap-8 laptop:rounded-2xl laptop:py-[30px] laptop:pl-10 laptop:pr-12"
            >
              {/**
               * ★브랜드 심볼 — 초판에 「자산이 없다」고 적고 첫 글자로 대체했는데
               * **리포에 이미 있었다**(`meta-colored-icon` · `PicoIcon` · `vive-icon`).
               * 없는 것(Apple · Google Play)만 첫 글자로 남긴다.
               */}
              <span className="flex size-8 shrink-0 items-center justify-center rounded-md text-sm font-600 laptop:size-[60px] laptop:text-2xl">
                <StoreIcon name={store.name} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5 laptop:gap-2">
                <span className="text-lg font-500 tracking-[0.15px] text-coolgray-100 laptop:text-3xl laptop:font-400 laptop:tracking-normal laptop:text-coolgray-90">
                  {store.name}
                </span>
                {store.caption && (
                  <span className="text-sm tracking-[0.4px] text-coolgray-50 laptop:text-base laptop:tracking-[0.25px] laptop:text-coolgray-60">
                    {t(store.caption)}
                  </span>
                )}
              </span>
              {/**
               * 우측 표기 — ★**폭마다 다르다**(시안 개정 2026-09-03).
               *
               * ```
               *          데스크탑            모바일
               *   PC     Download 버튼       Download 버튼
               *   VR     외부링크 아이콘      ★Download 버튼
               *   Mobile 외부링크 아이콘      ★Download 버튼
               * ```
               *
               * 종전 구현은 `storeAction` 하나로 **두 폭을 같이** 갈랐다. 개정판 모바일은
               * 7개 행 전부에 `btn / Download` 가 붙어 있어(`30035:23237`·`23244`·
               * `23263`·`23269`·`23275`·`23294`·`23301`) 한 행짜리 목업 잔재가 아니다.
               *
               * 그래서 **모바일은 항상 버튼**이고 `storeAction` 은 데스크탑에서만 읽는다.
               */}
              <span
                className={cn(
                  "flex h-9 shrink-0 items-center justify-center gap-2.5 rounded-lg border border-coolgray-20 px-3 text-sm font-600 tracking-[0.4px] text-coolgray-90 laptop:h-[42px] laptop:rounded laptop:border-coolgray-90 laptop:pl-6 laptop:pr-4 laptop:tracking-[0.1px]",
                  section.storeAction === "external" && "laptop:hidden",
                )}
              >
                {t("Download")}
                {/* 시안 `29889:102350` — 버튼 안 18px 다운로드 아이콘(데스크탑만). */}
                <FileDownloadIcon className="hidden size-[18px] laptop:block" />
              </span>
              {section.storeAction === "external" && (
                <ExternalLinkIcon className="hidden size-6 shrink-0 text-coolgray-90 laptop:block" />
              )}
            </a>
          ))}
        </div>
      </div>

      {/* 기존 시트를 그대로 연다 — 같은 정보이고 페이지를 떠나지 않는다. */}
      {/* 플레이어별로 브랜드를 좁힌다 — 그대로 열면 무관한 헤드셋이 대부분이다. */}
      <CompatibleVrHeadsetsSheets
        open={sheetOpen}
        setOpen={setSheetOpen}
        audience={section.headsetAudience}
      />
    </section>
  );
}
